import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Clock, Users, MapPin, MessageCircle, Phone, Monitor, Video, BookOpen, Info, Briefcase } from "lucide-react";
import { CONTACT, WHATSAPP_TRAINING_HREF } from "../lib/contact";
import ImageSlot from "../components/ImageSlot";

const faceBullets = [
  { icon: MapPin, label: "Ataşehir, İstanbul" },
  { icon: Clock, label: "2 tam gün" },
  { icon: Users, label: "Birebir eğitim" },
  { icon: BookOpen, label: "Teori + uygulamalı pratik" },
  { icon: CheckCircle2, label: "Başlangıçtan ileri seviyeye kişiye özel ilerleme" },
  { icon: CheckCircle2, label: "Eğitim sonrasında online destek" },
];

const onlineBullets = [
  { icon: Users, label: "Kapalı WhatsApp grubu" },
  { icon: Video, label: "Telegram üzerinden video dersler ve tarifler" },
  { icon: MessageCircle, label: "Sürekli online destek" },
  { icon: CheckCircle2, label: "Kişiye özel ilerleme ve rehberlik" },
];

export default function Trainings() {
  return (
    <div data-testid="page-trainings">
      <section className="max-w-7xl mx-auto px-5 lg:px-10 pt-14 lg:pt-20 pb-10">
        <div className="eyebrow">Eğitim Programları</div>
        <h1 className="font-serif mt-4 text-4xl lg:text-6xl font-semibold tracking-tight leading-[1.08]">
          Ekşi mayanın temelinden <br className="hidden md:block" />
          <span className="italic" style={{ color: "var(--em-terracotta)" }}>ustalığa</span> uzanan yolculuk.
        </h1>
        <p className="mt-6 max-w-2xl text-base lg:text-lg text-[color:var(--em-espresso-soft)]">
          İki farklı eğitim formatımız var: <strong className="font-semibold text-[color:var(--em-espresso)]">Yüz Yüze Eğitim</strong> ve{" "}
          <strong className="font-semibold text-[color:var(--em-espresso)]">Online Eğitim</strong>. Hedefinize, zamanınıza ve
          bulunduğunuz seviyeye uygun olanı seçebilirsiniz.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 space-y-20 lg:space-y-28 pb-10">
        {/* FACE-TO-FACE */}
        <article className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start" data-testid="training-face-to-face">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: "var(--em-terracotta-light)", color: "var(--em-terracotta-hover)" }}>
              Birebir • Atölye
            </div>
            <h2 className="font-serif mt-5 text-3xl lg:text-4xl font-semibold">Yüz Yüze Eğitim</h2>
            <p className="mt-4 text-[color:var(--em-espresso-soft)] leading-relaxed">
              Ataşehir&apos;de iki tam gün süren, birebir ilerleyen bir eğitim. Teorinin yanında
              hamuru elinizde tanımayı; yoğurma, şekillendirme, mayalanma ve pişirme süreçlerini
              uygulamalı olarak öğrenirsiniz.
            </p>
            <p className="mt-3 text-[color:var(--em-espresso-soft)] leading-relaxed">
              Eğitim iki günle sınırlı kalmaz. Sonrasında kendi mutfağınızda yaptığınız
              uygulamalarda online iletişim ve rehberlikle sürecinizi desteklemeye devam ederiz.
            </p>
            <ul className="mt-7 grid sm:grid-cols-2 gap-3">
              {faceBullets.map((b) => (
                <li key={b.label} className="flex items-start gap-2.5 text-sm">
                  <b.icon size={16} className="mt-0.5 flex-shrink-0" style={{ color: "var(--em-terracotta)" }} />
                  <span>{b.label}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 p-5 rounded-sm border" style={{ borderColor: "var(--em-border)", background: "var(--em-stone)" }}>
              <div className="text-sm font-medium">
                Detaylı bilgi ve kayıt için iletişime geçin.
              </div>
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <Link to="/egitim-basvuru?type=face" className="btn-terracotta inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold" data-testid="face-apply-btn">
                  Başvuru Formu <ArrowRight size={14} />
                </Link>
                <a href={WHATSAPP_TRAINING_HREF} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[color:var(--em-espresso)] text-sm font-semibold hover:bg-[color:var(--em-espresso)] hover:text-white transition-colors" data-testid="face-whatsapp-btn">
                  <MessageCircle size={14} /> WhatsApp
                </a>
                <a href={CONTACT.phoneHref} className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[color:var(--em-espresso)] text-sm font-semibold hover:bg-[color:var(--em-espresso)] hover:text-white transition-colors" data-testid="face-phone-btn">
                  <Phone size={14} /> Telefon
                </a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2">
            <ImageSlot src="/images/atolye-1.webp" alt="Ataşehir atölyesinde yüz yüze eğitim" position="center 22%" className="aspect-[4/3] lg:aspect-[4/5] rounded-sm" />
          </div>
        </article>

        {/* BUSINESS CONSULTING */}
        <article className="-mt-6 lg:-mt-12 rounded-sm border p-7 lg:p-10 grid lg:grid-cols-12 gap-6 lg:gap-10 items-start" style={{ borderColor: "var(--em-border)", background: "var(--em-stone)" }} data-testid="training-business">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: "var(--em-terracotta-light)", color: "var(--em-terracotta-hover)" }}>
              <Briefcase size={13} /> İşletmelere Özel
            </div>
            <h2 className="font-serif mt-5 text-2xl lg:text-3xl font-semibold">İşletmelere Yönelik Danışmanlık ve Eğitim</h2>
            <p className="mt-4 text-[color:var(--em-espresso-soft)] leading-relaxed">
              İşletmenin ihtiyacına, üretim yapısına ve hedeflerine göre özel olarak planlanan bir
              danışmanlık ve eğitim hizmetidir. Bireysel birebir yüz yüze eğitimden ayrı bir
              hizmettir; kapsamı işletmeyle birlikte belirlenir.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="text-sm font-medium">Detaylı bilgi için iletişime geçin.</div>
            <div className="mt-4 flex flex-col sm:flex-row flex-wrap gap-3">
              <Link to="/egitim-basvuru?type=business" className="btn-terracotta inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold" data-testid="business-apply-btn">
                Başvuru Formu <ArrowRight size={14} />
              </Link>
              <a href={WHATSAPP_TRAINING_HREF} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[color:var(--em-espresso)] text-sm font-semibold hover:bg-[color:var(--em-espresso)] hover:text-white transition-colors" data-testid="business-whatsapp-btn">
                <MessageCircle size={14} /> WhatsApp
              </a>
              <a href={CONTACT.phoneHref} className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[color:var(--em-espresso)] text-sm font-semibold hover:bg-[color:var(--em-espresso)] hover:text-white transition-colors" data-testid="business-phone-btn">
                <Phone size={14} /> Telefon
              </a>
            </div>
          </div>
        </article>


        {/* ONLINE */}
        <article className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start" data-testid="training-online">
          <div className="lg:col-span-6" data-testid="student-breads">
            <div className="grid grid-cols-2 gap-3">
              {[
                { src: "/images/ogrenci-1.webp", pos: "center 55%", wide: true },
                { src: "/images/ogrenci-4.webp", pos: "center 30%" },
                { src: "/images/ogrenci-3.webp", pos: "center 35%" },
                { src: "/images/ogrenci-5.webp", pos: "center 35%" },
                { src: "/images/ogrenci-2.webp", pos: "center 30%" },
              ].map((p) => (
                <ImageSlot key={p.src} src={p.src} alt="Online eğitim öğrencisinin yaptığı ekşi mayalı ekmek" position={p.pos} className={`rounded-sm ${p.wide ? "col-span-2 aspect-[4/3]" : "aspect-square"}`} />
              ))}
            </div>
            <p className="mt-3 text-xs text-[color:var(--em-text-mute)]">Online eğitim öğrencilerimizin kendi mutfaklarında yaptığı ekmekler.</p>
          </div>

          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: "var(--em-terracotta-light)", color: "var(--em-terracotta-hover)" }}>
              <Monitor size={13} /> Online Program
            </div>
            <h2 className="font-serif mt-5 text-3xl lg:text-4xl font-semibold">Online Eğitim</h2>
            <p className="mt-4 text-[color:var(--em-espresso-soft)] leading-relaxed">
              Kendi mutfağınızda, kendi fırınınızla ilerlediğiniz kapsamlı bir eğitim. Kapalı
              WhatsApp grubunda sürekli destek, Telegram üzerinden video dersler ve tarifler.
            </p>
            <p className="mt-3 text-[color:var(--em-espresso-soft)] leading-relaxed">
              Online eğitim kişiye özel ilerleyen bir süreçtir. Haftada en az iki uygulama yapan
              öğrenciler eğitimi yaklaşık 1,5 ayda tamamlayabilir. Daha sık uygulama yapan
              öğrenciler süreci daha kısa sürede tamamlayabilir.
            </p>
            <p className="mt-3 inline-flex items-start gap-2 text-xs text-[color:var(--em-text-mute)]" data-testid="online-duration-note">
              <Info size={14} className="mt-0.5 flex-shrink-0" />
              Eğitimin en geç 2 ay içinde tamamlanması beklenir.
            </p>
            <ul className="mt-7 grid sm:grid-cols-2 gap-3">
              {onlineBullets.map((b) => (
                <li key={b.label} className="flex items-start gap-2.5 text-sm">
                  <b.icon size={16} className="mt-0.5 flex-shrink-0" style={{ color: "var(--em-terracotta)" }} />
                  <span>{b.label}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 p-5 rounded-sm border" style={{ borderColor: "var(--em-border)", background: "var(--em-stone)" }}>
              <div className="text-sm font-medium">
                Detaylı bilgi ve kayıt için iletişime geçin.
              </div>
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <Link to="/egitim-basvuru?type=online" className="btn-terracotta inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold" data-testid="online-apply-btn">
                  Başvuru Formu <ArrowRight size={14} />
                </Link>
                <a href={WHATSAPP_TRAINING_HREF} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[color:var(--em-espresso)] text-sm font-semibold hover:bg-[color:var(--em-espresso)] hover:text-white transition-colors" data-testid="online-whatsapp-btn">
                  <MessageCircle size={14} /> WhatsApp
                </a>
                <a href={CONTACT.phoneHref} className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[color:var(--em-espresso)] text-sm font-semibold hover:bg-[color:var(--em-espresso)] hover:text-white transition-colors" data-testid="online-phone-btn">
                  <Phone size={14} /> Telefon
                </a>
              </div>
            </div>
          </div>
        </article>
      </section>
    </div>
  );
}
